import { backendDataTypesHash, backendDataTypesItemsArray } from '@constants';
import { Box, TextField, Typography } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import DatePicker from 'components/DatePicker';
import { SingleValuePickerV2 } from 'components/SingleValuePicker';
import useLocalDispatch from 'hooks/useLocalDispatch';
import { useState, useRef, MouseEventHandler } from 'react';
import JsonViewer from 'shared/JsonViewer';
import { fetchDataToIntegrationServer } from 'thunk';
import { StyledDefaultPrimaryButton, StyledForm } from 'styles';
import { Item } from 'types';
import { calculateAlertSeverityByStatusCode } from 'utils';
import { useNotification } from 'hooks/useNotification';
import { useTenantRealmDropdownsFlow } from 'TenantModule/hooks/useTenantRealmDropdownsFlow';
import { TenantSelector } from 'TenantModule/components/TenantSelector';
import RealmSelector from 'SandboxModule/components/RealmSelector';

const stateId = 'fetch-data-to-integration-server-from-backend-card';

const defaultBackendDataType = backendDataTypesHash['mosaic-time_entry'];

export const FetchDataToIntegrationServerFromBackendForm: React.FC<Record<string, never>> = () => {
  const { dispatchLocal, selectLocalData } = useLocalDispatch();
  const { showSuccess, showError } = useNotification();
  const {
    isRequesting,
    data: stateData,
    error,
  } = selectLocalData<{
    status: number;
    data: string;
  }>({
    stateId,
  });

  // Tenant/realm selection state (shared dropdowns)
  const { selectedTenant, selectedRealm, onSelectTenantRealm, onSelectRealm } =
    useTenantRealmDropdownsFlow();
  const realmDropdownRef = useRef<HTMLDivElement>(null);
  const onTenantSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (e.currentTarget && realmDropdownRef.current) {
      onSelectTenantRealm({
        tenantDropdownTarget: e.currentTarget,
        realmDropdownTarget: realmDropdownRef.current,
      });
    }
  };
  const onRealmSelectorClick: MouseEventHandler<HTMLElement> = (e) => {
    if (selectedTenant) {
      onSelectRealm({ target: e.currentTarget, tenantId: selectedTenant.id });
    }
  };

  const [fetchDataFromBackendParams, setFetchDataFromBackendParams] = useState<{
    dataType: Item;
    targetServiceId: string;
    projectId: string;
    startDate: string;
  }>({
    dataType: defaultBackendDataType,
    targetServiceId: '',
    startDate: '',
    projectId: '',
  });

  const onFetchDataToISFromBackendValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    if (name === 'dataType') {
      setFetchDataFromBackendParams({
        ...fetchDataFromBackendParams,
        [name as string]: backendDataTypesHash[value],
      });
    } else {
      setFetchDataFromBackendParams({
        ...fetchDataFromBackendParams,
        [name]: value,
      });
    }
  };

  const onFetchDataToIsFromBackendClick: React.MouseEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();

    const { dataType, targetServiceId, projectId, startDate } = fetchDataFromBackendParams;
    if (targetServiceId) {
      const params = {
        targetServiceId: Number(targetServiceId),
        dataType: dataType.value as string,
        startDate: startDate ? startDate : '',
        projectId: projectId ? String(projectId) : '',
        fetchFrom: 'backend',
        tenantName: selectedTenant?.tenantName || undefined,
        realmId: selectedRealm?.realm_id || undefined,
      };
      dispatchLocal(fetchDataToIntegrationServer, {
        params,
        stateId,
        formatSuccessResponse: ({ response }) => {
          return {
            status: response.status,
            data: response.data,
          };
        },
        formatFailureResponse: ({ error }: { error: any }) => {
          return {
            status: error.status,
            data: error.data,
          };
        },
        onSuccessCallback: ({ response }) => {
          const error = response.data?.errors?.[0];
          if (error) {
            showError(`Failed to trigger fetch data. ${error}`);
          } else {
            showSuccess('Successfully triggered fetch data.');
          }
        },
      });
    }
  };

  return (
    <>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1.125rem', mb: 1 }}>
        Fetch Data to Integration Server
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2,
        }}
      >
        From Backend
      </Typography>
      <StyledForm onSubmit={onFetchDataToIsFromBackendClick}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Organization
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                gap: 2,
              }}
            >
              {selectedTenant ? (
                <TenantSelector onClick={onTenantSelectorClick} tenantId={selectedTenant.id} />
              ) : (
                <TenantSelector onClick={onTenantSelectorClick} tenantId={1} />
              )}
              <RealmSelector
                onClick={onRealmSelectorClick}
                realmId={selectedRealm?.realm_id || ''}
                ref={realmDropdownRef}
              />
            </Box>
          </Box>
          <SingleValuePickerV2
            items={backendDataTypesItemsArray}
            item={String(fetchDataFromBackendParams.dataType.value)}
            itemsHash={backendDataTypesHash}
            inputLabel="Data Type"
            inputName="dataType"
            onValueChange={onFetchDataToISFromBackendValueChange}
          />
          <TextField
            label="Integration ID"
            type="number"
            name="targetServiceId"
            value={fetchDataFromBackendParams.targetServiceId}
            onChange={onFetchDataToISFromBackendValueChange}
            required
          />
          <TextField
            label="Project Id (optional)"
            type="number"
            name="projectId"
            value={fetchDataFromBackendParams.projectId}
            onChange={onFetchDataToISFromBackendValueChange}
          />
          <DatePicker
            label="Start Date (optional)"
            value={fetchDataFromBackendParams.startDate}
            onChange={(newDate) =>
              setFetchDataFromBackendParams((prev) => ({ ...prev, startDate: newDate ?? '' }))
            }
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 6,
              mt: 'auto',
            }}
          >
            {!isRequesting && stateData && (
              <Alert severity={calculateAlertSeverityByStatusCode(stateData.status)}>
                <AlertTitle>{stateData.status}</AlertTitle>
                <JsonViewer collapsed src={{ response: stateData?.data || '' }} />
              </Alert>
            )}
            <StyledDefaultPrimaryButton variant="contained" type="submit" disabled={isRequesting}>
              Request
            </StyledDefaultPrimaryButton>
          </Box>
        </Box>
      </StyledForm>
    </>
  );
};
